import React, { useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';

const font = "'DM Sans', sans-serif";

function hashId(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default function RedNeuronal2D({
  emprendedores = [],
  pensamientos = [],
  sinapsis = [],
  userId,
  onClickPensamiento,
  onClickEmprendedor,
  highlightWikilinks = false,
  hubLabel,
  modoPrivado = false,
}) {
  const svgRef = useRef(null);
  const nodosRef = useRef({});
  const rafRef = useRef(null);
  const draggingRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const didDrag = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const panningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const groupRef = useRef(null);

  // Build and animate
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // Clear previous content
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    // Defs
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    defs.innerHTML = `
      <radialGradient id="g-hub" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#FDBE02"/>
        <stop offset="100%" stop-color="#eb636b"/>
      </radialGradient>
      <filter id="brillo" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    `;
    svg.appendChild(defs);

    // Main group for zoom/pan
    const mainGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    mainGroup.setAttribute('id', 'main-group');
    svg.appendChild(mainGroup);
    groupRef.current = mainGroup;

    // Build nodos
    const CX = 420, CY = 300;
    const nodos = {};

    nodos.td = { x: CX, y: CY, bx: CX, by: CY, vx: 0, vy: 0, r: modoPrivado ? 28 : 22, tipo: 'hub', fijo: true, label: hubLabel || 'T&D' };

    const count = emprendedores.length || 1;
    emprendedores.forEach((emp, i) => {
      const uid = emp.id || emp.user_id;
      const seed = hashId(uid);
      const angBase = (Math.PI * 2 / count) * i;
      const ang = angBase - Math.PI / 2;
      const radio = 300 + count * 15;
      const x = CX + Math.cos(ang) * radio;
      const y = CY + Math.sin(ang) * radio;
      nodos[uid] = {
        x, y, bx: x, by: y, vx: 0, vy: 0, r: uid === userId ? 26 : 22, tipo: 'emp', fijo: false,
        label: (emp.full_name || emp.business_name || 'Emp').split(' ')[0].slice(0, 8),
        fill: emp.identidad === 'thinker' ? '#FDBE02' : emp.identidad === 'doer' ? '#5dc1b9' : (uid === userId ? '#FDBE02' : 'rgba(255,255,255,0.35)'),
        userId: uid,
      };
    });

    // Build chain map: pensamiento → its parent pensamiento (via continuacion/wikilink from same user)
    const chainParent = {};
    sinapsis.forEach(s => {
      if ((s.tipo === 'continuacion' || s.tipo === 'wikilink') && s.from_id !== s.to_id) {
        // to_id chains off from_id (from_id is the parent thought)
        if (s.tipo === 'continuacion') chainParent[s.to_id] = s.from_id;
        // wikilink: from_id is parent (referenciada), to_id is child (actual)
        else chainParent[s.to_id] = s.from_id;
      }
    });

    console.log('🔍 [RED] chainParent:', JSON.stringify(chainParent));
    console.log('🔍 [RED] sinapsis wikilinks:', sinapsis.filter(s => s.tipo === 'wikilink').map(s => s.from_id?.slice(0,8) + '→' + s.to_id?.slice(0,8)));
    console.log('🔍 [RED] pensamientos:', pensamientos.map(p => p.id.slice(0,8) + ' "' + p.titulo + '"'));
    console.log('🔍 [RED] nodos keys:', Object.keys(nodos).map(k => k.slice(0,8)));

    // Sort: process pensamientos without chainParent first, then chained ones
    const sortedPens = [...pensamientos].sort((a, b) => {
      const aChained = chainParent[a.id] ? 1 : 0;
      const bChained = chainParent[b.id] ? 1 : 0;
      return aChained - bChained;
    });

    // Track how many pensamientos each emprendedor has, for even spacing
    const empPensCount = {};
    const empPensIdx = {};

    sortedPens.forEach((p) => {
      const empPadre = nodos[p.user_id] || (modoPrivado ? nodos.td : null);
      if (!empPadre) return;
      const uid = p.user_id;
      if (!empPensCount[uid]) {
        empPensCount[uid] = pensamientos.filter(pp => pp.user_id === uid).length;
        empPensIdx[uid] = 0;
      }
      const parentId = chainParent[p.id];
      const parentNodo = parentId ? nodos[parentId] : null;
      const seed = hashId(p.id);
      let ancla, ang, rad;

      if (parentNodo) {
        // Chained: extend outward from parent, same direction as parent→empPadre but further out
        const dx = parentNodo.bx - empPadre.bx;
        const dy = parentNodo.by - empPadre.by;
        const baseAng = Math.atan2(dy, dx);
        ang = baseAng + ((seed % 100) / 100 - 0.5) * 0.4;
        rad = 55;
        ancla = parentNodo;
      } else {
        // Root pensamiento: space evenly in a half-arc AWAY from T&D hub
        const total = empPensCount[uid] || 1;
        const idx = empPensIdx[uid]++;
        // Direction from hub to emprendedor
        const hubAng = Math.atan2(empPadre.by - CY, empPadre.bx - CX);
        // Fan out in a 180° arc on the far side from the hub
        const spread = Math.PI * 0.8;
        const startAng = hubAng - spread / 2;
        ang = startAng + (spread / Math.max(total - 1, 1)) * idx;
        rad = 65;
        ancla = empPadre;
      }

      const x = ancla.bx + Math.cos(ang) * rad;
      const y = ancla.by + Math.sin(ang) * rad;
      // Radius scales with sinapsis count
      const sinCount = sinapsis.filter(s => s.from_id === p.id || s.to_id === p.id).length;
      const dynR = sinCount >= 10 ? 18 : sinCount >= 5 ? 15 : sinCount >= 2 ? 12 : 9;
      nodos[p.id] = {
        x, y, bx: x, by: y, vx: 0, vy: 0, r: dynR, tipo: 'pens', fijo: false,
        fill: empPadre.fill || (modoPrivado ? '#AFA9EC' : '#5dc1b9'), titulo: p.titulo, pensamientoId: p.id, sinCount,
        parentId: chainParent[p.id] || p.user_id,
      };
    });

    nodosRef.current = nodos;

    // Draw lines — skip cluster lines for pensamientos that have a chainParent (they already have a wikilink line)
    const chainedIds = new Set(Object.keys(chainParent));
    sinapsis.forEach((s, idx) => {
      // Skip cluster for chained pensamientos to avoid triangle
      if ((s.tipo === 'cluster' || s.tipo === 'continuacion') && chainedIds.has(s.from_id)) return;
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('id', `linea-${idx}`);
      let stroke = '#5dc1b9', width = '1', dash = '', op = '0.55';
      if (s.tipo === 'cluster' || s.tipo === 'continuacion') { stroke = '#FDBE02'; width = '1'; dash = '3 3'; op = '0.5'; }
      else if (s.tipo === 'wikilink') { stroke = '#5dc1b9'; width = '2.5'; dash = '6 3'; op = highlightWikilinks ? '1' : '0.8'; }
      else if (s.tipo === 'afinidad') { stroke = '#FDBE02'; width = '2'; dash = '5 3'; op = '0.7'; }
      line.setAttribute('stroke', stroke);
      line.setAttribute('stroke-width', width);
      if (dash) line.setAttribute('stroke-dasharray', dash);
      line.setAttribute('opacity', op);
      line.setAttribute('fill', 'none');
      mainGroup.appendChild(line);
    });

    // Draw nodes
    Object.entries(nodos).forEach(([id, n]) => {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('id', `nodo-${id}`);
      g.setAttribute('transform', `translate(${n.x},${n.y})`);
      g.style.cursor = n.tipo === 'hub' ? 'default' : 'grab';

      if (n.tipo === 'hub') {
        const glowColor = modoPrivado ? '#AFA9EC' : '#FDBE02';
        const fillColor = modoPrivado ? '#534AB7' : 'url(#g-hub)';
        const textColor = modoPrivado ? '#fff' : '#0a0a0a';
        const glow = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        glow.setAttribute('r', n.r + 12); glow.setAttribute('fill', glowColor); glow.setAttribute('opacity', '0.18');
        g.appendChild(glow);
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        c.setAttribute('r', n.r); c.setAttribute('fill', fillColor); c.setAttribute('filter', 'url(#brillo)');
        if (modoPrivado) { c.setAttribute('stroke', '#AFA9EC'); c.setAttribute('stroke-width', '1.5'); }
        g.appendChild(c);
        const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        txt.setAttribute('text-anchor', 'middle'); txt.setAttribute('dominant-baseline', 'middle');
        txt.setAttribute('font-size', modoPrivado ? '13' : '11'); txt.setAttribute('font-weight', '600'); txt.setAttribute('fill', textColor);
        txt.setAttribute('font-family', font); txt.style.pointerEvents = 'none';
        txt.textContent = n.label;
        g.appendChild(txt);
      } else if (n.tipo === 'emp') {
        const dark = n.fill === '#FDBE02';
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        c.setAttribute('r', n.r); c.setAttribute('fill', n.fill); c.setAttribute('filter', 'url(#brillo)');
        if (n.userId === userId) { c.setAttribute('stroke', '#fff'); c.setAttribute('stroke-width', '1.5'); }
        g.appendChild(c);
        const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        txt.setAttribute('text-anchor', 'middle'); txt.setAttribute('dominant-baseline', 'middle');
        txt.setAttribute('font-size', n.userId === userId ? '13' : '12');
        txt.setAttribute('font-weight', n.userId === userId ? '600' : '500');
        txt.setAttribute('fill', dark ? '#0a0a0a' : '#fff');
        txt.setAttribute('font-family', font); txt.style.pointerEvents = 'none';
        txt.textContent = n.label;
        g.appendChild(txt);
        // Click handler — only fire if no real drag occurred
        g.addEventListener('click', () => { if (!didDrag.current) onClickEmprendedor?.(n.userId); });
        g.addEventListener('mousedown', (e) => { e.preventDefault(); draggingRef.current = id; didDrag.current = false; dragStartPos.current = { x: e.clientX, y: e.clientY }; const pt = svgPt(e); dragOffsetRef.current = { x: n.x - pt.x, y: n.y - pt.y }; });
        g.addEventListener('touchstart', (e) => { e.preventDefault(); draggingRef.current = id; didDrag.current = false; dragStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; const pt = svgPt(e.touches[0]); dragOffsetRef.current = { x: n.x - pt.x, y: n.y - pt.y }; }, { passive: false });
      } else {
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        c.setAttribute('r', n.r); c.setAttribute('fill', n.fill); c.setAttribute('opacity', '0.65');
        c.setAttribute('stroke', n.fill); c.setAttribute('stroke-width', '1.2'); c.setAttribute('filter', 'url(#brillo)');
        g.appendChild(c);
        // Title below
        if (n.titulo) {
          const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          txt.setAttribute('text-anchor', 'middle'); txt.setAttribute('y', n.r + 14);
          txt.setAttribute('font-size', '10'); txt.setAttribute('fill', 'rgba(255,255,255,0.75)');
          txt.setAttribute('font-family', font); txt.style.pointerEvents = 'none';
          txt.textContent = n.titulo.length > 25 ? n.titulo.slice(0, 25) + '...' : n.titulo;
          g.appendChild(txt);
        }
        g.addEventListener('click', () => { if (!didDrag.current) onClickPensamiento?.(n.pensamientoId); });
        g.addEventListener('mousedown', (e) => { e.preventDefault(); draggingRef.current = id; didDrag.current = false; dragStartPos.current = { x: e.clientX, y: e.clientY }; const pt = svgPt(e); dragOffsetRef.current = { x: n.x - pt.x, y: n.y - pt.y }; });
        g.addEventListener('touchstart', (e) => { e.preventDefault(); draggingRef.current = id; didDrag.current = false; dragStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; const pt = svgPt(e.touches[0]); dragOffsetRef.current = { x: n.x - pt.x, y: n.y - pt.y }; }, { passive: false });
      }
      mainGroup.appendChild(g);
    });

    // SVG point helper
    function svgPt(e) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      return pt.matrixTransform(svg.getScreenCTM().inverse());
    }

    // Pinch zoom state
    let pinchInitDist = 0, pinchInitScale = 1, isPinching = false;

    // Mouse/touch handlers on SVG for drag + pan + pinch
    function onMove(e) {
      // Pinch zoom (2 fingers)
      if (e.touches && e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (isPinching && pinchInitDist > 0) {
          zoomRef.current = Math.max(0.3, Math.min(3, pinchInitScale * (dist / pinchInitDist)));
        }
        return;
      }
      const ev = e.touches ? e.touches[0] : e;
      if (draggingRef.current) {
        const dx = ev.clientX - dragStartPos.current.x, dy = ev.clientY - dragStartPos.current.y;
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) didDrag.current = true;
        const pt = svgPt(ev);
        const n = nodosRef.current[draggingRef.current];
        if (n) {
          const newBx = pt.x + dragOffsetRef.current.x;
          const newBy = pt.y + dragOffsetRef.current.y;
          const dbx = newBx - n.bx, dby = newBy - n.by;
          n.bx = newBx; n.by = newBy; n.vx = 0; n.vy = 0;
          // If dragging an emp node, move its child pensamientos too
          if (n.tipo === 'emp') {
            for (const cid of Object.keys(nodosRef.current)) {
              const child = nodosRef.current[cid];
              if (child.parentId === draggingRef.current) {
                child.bx += dbx; child.by += dby; child.vx = 0; child.vy = 0;
              }
            }
          }
        }
      } else if (panningRef.current) {
        panRef.current = {
          x: panStartRef.current.px + (ev.clientX - panStartRef.current.x) / zoomRef.current,
          y: panStartRef.current.py + (ev.clientY - panStartRef.current.y) / zoomRef.current,
        };
      }
    }
    function onUp() {
      if (draggingRef.current && userId) {
        const n = nodosRef.current[draggingRef.current];
        if (n) supabase.from('posiciones_red').upsert({ user_id: userId, nodo_id: draggingRef.current, x: n.bx, y: n.by }).then(() => {});
      }
      draggingRef.current = null;
      panningRef.current = false;
      isPinching = false;
    }
    function onBgDown(e) {
      // Pinch start (2 fingers)
      if (e.touches && e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchInitDist = Math.sqrt(dx * dx + dy * dy);
        pinchInitScale = zoomRef.current;
        isPinching = true;
        return;
      }
      if (e.target === svg) {
        const ev = e.touches ? e.touches[0] : e;
        panStartRef.current = { x: ev.clientX, y: ev.clientY, px: panRef.current.x, py: panRef.current.y };
        panningRef.current = true;
      }
    }
    function onWheel(e) {
      e.preventDefault();
      zoomRef.current = Math.min(3, Math.max(0.3, zoomRef.current + (e.deltaY > 0 ? -0.08 : 0.08)));
    }

    svg.addEventListener('mousemove', onMove);
    svg.addEventListener('mouseup', onUp);
    svg.addEventListener('touchmove', onMove, { passive: false });
    svg.addEventListener('touchend', onUp);
    svg.addEventListener('mousedown', onBgDown);
    svg.addEventListener('touchstart', onBgDown, { passive: false });
    svg.addEventListener('wheel', onWheel, { passive: false });

    // Animation loop — direct DOM manipulation
    let t = 0;
    const animar = () => {
      t += 0.016;
      const n = nodosRef.current;
      const ids = Object.keys(n);

      // Repulsion (gentle — just prevent overlap)
      for (let i = 0; i < ids.length; i++) {
        const a = n[ids[i]];
        if (a.fijo || ids[i] === draggingRef.current) continue;
        for (let j = 0; j < ids.length; j++) {
          if (i === j) continue;
          const b = n[ids[j]];
          const dx = b.bx - a.bx, dy = b.by - a.by;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const minDist = (a.r + b.r) * 2.5;
          if (dist < minDist) {
            const f = (minDist - dist) / dist * 0.15;
            a.vx -= dx * f * 0.03;
            a.vy -= dy * f * 0.03;
          }
        }
      }

      // Attraction: pensamientos stay near their parent
      for (const id of ids) {
        const node = n[id];
        if (node.fijo || node.tipo !== 'pens' || id === draggingRef.current) continue;
        const parent = n[node.parentId];
        if (!parent) continue;
        const dx = parent.bx - node.bx, dy = parent.by - node.by;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const idealDist = 80;
        if (dist > idealDist * 1.5) {
          const f = (dist - idealDist) / dist * 0.02;
          node.vx += dx * f;
          node.vy += dy * f;
        }
      }

      // Apply velocity + subtle breathing
      for (const id of ids) {
        const node = n[id];
        if (node.fijo) { node.x = node.bx; node.y = node.by; }
        else if (id !== draggingRef.current) {
          node.bx += node.vx;
          node.by += node.vy;
          node.vx *= 0.9;
          node.vy *= 0.9;
          const speed = 0.3;
          const amp = node.tipo === 'pens' ? 4 : 3;
          const offset = (hashId(id) % 1000) / 100;
          node.x = node.bx + Math.sin((t + offset) * speed) * amp;
          node.y = node.by + Math.cos((t + offset) * speed * 0.9) * amp;
        } else {
          node.x = node.bx; node.y = node.by;
        }

        // Update DOM
        const el = document.getElementById(`nodo-${id}`);
        if (el) el.setAttribute('transform', `translate(${node.x},${node.y})`);
      }

      // Update lines
      sinapsis.forEach((s, idx) => {
        const a = n[s.from_id], b = n[s.to_id];
        if (!a || !b) return;
        const line = document.getElementById(`linea-${idx}`);
        if (line) { line.setAttribute('x1', a.x); line.setAttribute('y1', a.y); line.setAttribute('x2', b.x); line.setAttribute('y2', b.y); }
      });

      // Update zoom/pan transform
      if (groupRef.current) {
        groupRef.current.setAttribute('transform', `translate(${panRef.current.x},${panRef.current.y}) scale(${zoomRef.current})`);
      }

      rafRef.current = requestAnimationFrame(animar);
    };
    rafRef.current = requestAnimationFrame(animar);

    return () => {
      cancelAnimationFrame(rafRef.current);
      svg.removeEventListener('mousemove', onMove);
      svg.removeEventListener('mouseup', onUp);
      svg.removeEventListener('touchmove', onMove);
      svg.removeEventListener('touchend', onUp);
      svg.removeEventListener('mousedown', onBgDown);
      svg.removeEventListener('touchstart', onBgDown);
      svg.removeEventListener('wheel', onWheel);
    };
    // eslint-disable-next-line
  }, [emprendedores?.length, pensamientos?.length, sinapsis?.length, userId]);

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={svgRef} viewBox="0 0 840 600" style={{ width: '100%', height: 500, display: 'block', userSelect: 'none', touchAction: 'none', background: 'transparent' }} />
      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, zIndex: 5 }}>
        {[
          { label: '−', fn: () => { zoomRef.current = Math.max(0.3, zoomRef.current - 0.2); } },
          { label: '+', fn: () => { zoomRef.current = Math.min(3, zoomRef.current + 0.2); } },
          { label: '↻', fn: () => { zoomRef.current = 1; panRef.current = { x: 0, y: 0 }; } },
        ].map((b, i) => (
          <button key={i} onClick={b.fn} style={{ width: 28, height: 28, background: 'rgba(10,10,20,0.7)', backdropFilter: 'blur(8px)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 6, color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font }}>{b.label}</button>
        ))}
      </div>
    </div>
  );
}
